import { useIsMutating } from '@eigtech/ui-shared-api'
import { mostReadable, stringToColor } from '@eigtech/ui-shared-color'
import { AspectRatio } from '@eigtech/ui/components/aspect-ratio'
import { Avatar, AvatarFallback, getInitials } from '@eigtech/ui/components/avatar'
import { Button } from '@eigtech/ui/components/button'
import { Spinner } from '@eigtech/ui/components/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { cn } from '@eigtech/ui/lib/utils'
import { Camera } from 'lucide-react'
import { ReactNode, useMemo } from 'react'
import { useShallow } from 'zustand/shallow'
import { meetingsMutationKeys } from '../../api/constants'
import { MeetingState, useMeetingStore } from '../../context/MeetingState'
import { useCaptureImageFromVideo } from '../../hooks/useCaptureImageFromVideo'
import { useFilteredRoster } from '../../hooks/useRoster'
import { MicActivity } from '../Status/MicActivity'

type ObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'

export type DeprecatedVideoTileProps = {
  actionsSlot?: ReactNode
  aspectRatio?: number | false
  attendeeId?: string
  className?: string
  id?: string
  isActiveVideo?: boolean
  name?: string | null
  objectFit?: ObjectFit
  ref?: React.Ref<HTMLVideoElement>
  tileId?: number
}

export function DeprecatedVideoTile({
  ref,
  className,
  aspectRatio = 16 / 9,
  attendeeId: baseAttendeeId,
  name: fallbackName,
  tileId,
  actionsSlot,
  isActiveVideo,
  ...props
}: DeprecatedVideoTileProps) {
  const roster = useFilteredRoster()

  const { attendeeId, name } = useMemo(() => {
    const attendeeId = baseAttendeeId?.replace('#content', '')
    const name = (!!attendeeId && roster[attendeeId.replace('#content', '')]?.name) ?? fallbackName

    return { attendeeId, name }
  }, [baseAttendeeId, fallbackName, roster])

  const canScreenshot = useMeetingStore(canScreenshotStateSelector)
  const isMeAndActive = props.id === 'local' && isActiveVideo

  return (
    <>
      <div
        className={cn(
          'flex max-w-full flex-col space-y-1 overflow-hidden rounded bg-black',
          className
        )}
        {...props}
      >
        <div className="relative grow">
          {!!name && <UserAvatar name={name} />}

          {aspectRatio === false ? (
            <video ref={ref} className="h-full w-full object-contain" />
          ) : (
            <AspectRatio ratio={aspectRatio}>
              <video ref={ref} className="h-full w-full object-contain" />
            </AspectRatio>
          )}
          {canScreenshot && tileId !== undefined && isMeAndActive && (
            <Screenshot isActiveVideo isLocal tileId={tileId} />
          )}
        </div>

        <div className="flow-row flex w-full items-center justify-between space-x-2 px-2 pb-1">
          <header className="flex min-w-0 shrink grow items-center truncate">
            {name && (
              <Tooltip>
                <TooltipTrigger>
                  <p className="truncate text-[0.75em] text-white">{name}</p>
                </TooltipTrigger>

                <TooltipContent>{name}</TooltipContent>
              </Tooltip>
            )}
          </header>

          <div className="flex items-center">
            {!!attendeeId && <MicActivity attendeeId={attendeeId} className="mr-0.5 py-1" />}
            {canScreenshot && tileId !== undefined && !isMeAndActive && (
              <Screenshot isActiveVideo={isActiveVideo} tileId={tileId} />
            )}
            {actionsSlot}
          </div>
        </div>
      </div>
    </>
  )
}

function Screenshot({
  tileId,
  isLocal,
  isActiveVideo,
}: {
  tileId: number
  isLocal?: boolean
  isActiveVideo?: boolean
}) {
  const meetingDetails = useMeetingStore(useShallow(meetingDetailsStateSelector))

  const { canvasRef, downloadRef, capturePhoto } = useCaptureImageFromVideo({ tileId })

  const isUploading = useIsMutating({
    mutationKey: meetingsMutationKeys.uploadScreenshot({
      entityId: meetingDetails?.entityId ?? 'corn:unknown:unknown:unknown',
      meetingId: meetingDetails?.meetingId ?? '',
    }),
  })
  return (
    <>
      {!!isUploading && <Spinner className="size-[1.1em]" />}

      <Tooltip>
        <TooltipTrigger
          render={(props) =>
            isLocal && isActiveVideo ? (
              <Button
                {...props}
                aria-label="Capture photo."
                className="absolute bottom-1 right-1.5 size-[3em] shadow-2xl"
                mode="icon"
                onClick={capturePhoto}
              >
                <Camera className="inherit size-[1.75em] text-[inherit]" />
              </Button>
            ) : (
              <Button
                {...props}
                aria-label="Capture photo."
                className="size-[1.75em] text-white shadow-2xl"
                mode="icon"
                size="sm"
                variant="ghost"
                onClick={capturePhoto}
              >
                <Camera className="inherit size-[1.25em] text-[inherit]" />
              </Button>
            )
          }
        ></TooltipTrigger>

        <TooltipContent>Capture photo</TooltipContent>
      </Tooltip>

      <canvas ref={canvasRef} className="hidden" />
      <a ref={downloadRef} className="hidden" />
    </>
  )
}

function UserAvatar({ name, ...props }: { name: string } & React.ComponentProps<typeof Avatar>) {
  const color = stringToColor(name)
  const fontColor = mostReadable(color, ['#fff', '#000'])?.toHexString()

  return (
    <div className="absolute flex h-full w-full items-center justify-center">
      <Avatar className="size-sm" style={{ backgroundColor: color, color: fontColor }} {...props}>
        <AvatarFallback>{getInitials(name)}</AvatarFallback>
      </Avatar>
    </div>
  )
}

const canScreenshotStateSelector = (state: MeetingState) => state.canScreenshot
const meetingDetailsStateSelector = (state: MeetingState) => state.meetingDetails
